import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  asciiHeader,
  bootHint,
  commandDefinitions,
  defaultTheme,
  neofetchContent,
  portfolioContent,
  primaryCommandButtons,
  readmeContent,
  terminalPrompt,
  TerminalThemeName,
  themeDefinitions,
} from '../data/terminalContent';
import { CommandHistoryItem, TerminalEntry, TerminalLine } from '../types/terminal';
import { BootSequence } from './BootSequence';
import { CommandInput } from './CommandInput';
import { CommandOutput } from './CommandOutput';
import { AsteroidsGame } from './games/AsteroidsGame';
import { DinoGame } from './games/DinoGame';
import { PongGame } from './games/PongGame';
import { SnakeGame } from './games/SnakeGame';
import { SpaceInvadersGame } from './games/SpaceInvadersGame';
import { GameName } from './games/types';
import { MatrixRain } from './MatrixRain';

const MATRIX_DURATION_MS = 6000;
const MELTDOWN_RECOVERY_DELAY_MS = 500;
const TYPEWRITER_DELAY_MS = 4;
const THEME_STORAGE_KEY = 'ak_terminal_theme';
const VISITED_STORAGE_KEY = 'ak_visited';

const LA_TIMEZONE = 'America/Los_Angeles';
const clockFormatter = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: LA_TIMEZONE,
  timeZoneName: 'short',
});
const historyTimeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: LA_TIMEZONE,
});

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const isThemeName = (value: string): value is TerminalThemeName => value in themeDefinitions;
const gameNames: GameName[] = ['snake', 'pong', 'dino', 'asteroids', 'invaders'];
const isGameName = (value: string): value is GameName => gameNames.includes(value as GameName);
const gameActions = gameNames.map((game) => ({ label: game, command: `play ${game}` }));
const themeActions = (Object.keys(themeDefinitions) as TerminalThemeName[]).map((themeName) => ({
  label: themeName,
  command: `theme ${themeName}`,
}));

const resolvePlayCommand = (
  loweredCommand: string
):
  | { action: 'none' }
  | { action: 'list'; message: string }
  | { action: 'invalid'; message: string }
  | { action: 'start'; game: GameName } => {
  if (loweredCommand === 'play') {
    return { action: 'list', message: 'Available games: snake, pong, dino, asteroids, invaders' };
  }

  if (!loweredCommand.startsWith('play ')) {
    return { action: 'none' };
  }

  const target = loweredCommand.slice(5).trim();
  if (!target || target.includes(' ')) {
    return { action: 'invalid', message: 'Unknown game. Available: snake, pong, dino, asteroids, invaders' };
  }

  if (!isGameName(target)) {
    return { action: 'invalid', message: 'Unknown game. Available: snake, pong, dino, asteroids, invaders' };
  }

  return { action: 'start', game: target };
};

const resolveInitialTheme = (): TerminalThemeName => {
  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme && isThemeName(storedTheme)) {
      return storedTheme;
    }
  } catch {
    return defaultTheme;
  }

  return defaultTheme;
};

const resolveShouldSkipBoot = (): boolean => {
  try {
    return window.localStorage.getItem(VISITED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const getHeaderClockLabel = () => {
  const parts = clockFormatter.formatToParts(new Date());
  const time = [
    parts.find((part) => part.type === 'hour')?.value ?? '00',
    parts.find((part) => part.type === 'minute')?.value ?? '00',
    parts.find((part) => part.type === 'second')?.value ?? '00',
  ].join(':');
  const timezone = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'PT';
  return `${time} ${timezone}`;
};

export function Terminal() {
  const [skipBoot] = useState(resolveShouldSkipBoot);
  const [bootComplete, setBootComplete] = useState(false);
  const [entries, setEntries] = useState<TerminalEntry[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [commandHistory, setCommandHistory] = useState<CommandHistoryItem[]>([]);
  const [, setHistoryIndex] = useState<number | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [matrixActive, setMatrixActive] = useState(false);
  const [meltdownActive, setMeltdownActive] = useState(false);
  const [welcomeShown, setWelcomeShown] = useState(false);
  const [theme, setTheme] = useState<TerminalThemeName>(resolveInitialTheme);
  const [typingCursor, setTypingCursor] = useState<{ entryId: number; lineIndex: number } | null>(null);
  const [headerClock, setHeaderClock] = useState(getHeaderClockLabel);
  const [hashCommandLoaded, setHashCommandLoaded] = useState(false);
  const [gameActive, setGameActive] = useState<GameName | null>(null);

  const viewportRef = useRef<HTMLDivElement>(null);
  const commandInputRef = useRef<HTMLInputElement>(null);
  const entryIdRef = useRef(0);
  const matrixTimeoutRef = useRef<number | null>(null);
  const isBusyRef = useRef(isBusy);

  const nextId = useCallback(() => {
    entryIdRef.current += 1;
    return entryIdRef.current;
  }, []);

  const activeTheme = themeDefinitions[theme];

  const focusCommandInput = useCallback(() => {
    window.setTimeout(() => {
      commandInputRef.current?.focus();
    }, 0);
  }, []);

  const shellStyle = useMemo(
    () =>
      ({
        '--terminal-bg': activeTheme.bg,
        '--terminal-bg-glow': activeTheme.bgGlow,
        '--terminal-fg': activeTheme.fg,
        '--terminal-fg-soft': activeTheme.fgSoft,
        '--terminal-rgb': activeTheme.rgb,
        '--terminal-border': activeTheme.border,
      }) as CSSProperties,
    [activeTheme]
  );

  const formatHistoryTimestamp = useCallback((timestamp: number) => historyTimeFormatter.format(new Date(timestamp)), []);

  const buildNeofetchRows = useCallback(
    (themeName: TerminalThemeName) => {
      const resolvedStats = neofetchContent.stats.map((line) => ({
        ...line,
        value: line.value.replace('{theme}', themeName),
      }));

      const rows = [] as {
        left: string;
        label?: string;
        value: string;
      }[];
      const totalRows = Math.max(neofetchContent.ascii.length, resolvedStats.length);

      for (let index = 0; index < totalRows; index += 1) {
        const stat = resolvedStats[index];
        rows.push({
          left: neofetchContent.ascii[index] ?? '',
          label: stat?.label ? `${stat.label}:` : undefined,
          value: stat?.value ?? '',
        });
      }

      return rows;
    },
    []
  );

  const fetchWeatherLine = useCallback(async (): Promise<TerminalLine> => {
    try {
      const response = await fetch('https://wttr.in/Las+Vegas?format=%C+%t+%h+%w');
      if (!response.ok) {
        throw new Error('Request failed');
      }

      const weatherText = (await response.text()).trim();
      return { text: `Las Vegas weather: ${weatherText}` };
    } catch {
      return { text: 'Weather service unavailable.', tone: 'error' };
    }
  }, []);

  useEffect(() => {
    isBusyRef.current = isBusy;
  }, [isBusy]);

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage write failures.
    }
  }, [theme]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setHeaderClock(getHeaderClockLabel());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const handleBootComplete = useCallback(() => {
    setBootComplete(true);
    try {
      window.localStorage.setItem(VISITED_STORAGE_KEY, 'true');
    } catch {
      // Ignore storage write failures.
    }
    focusCommandInput();
  }, [focusCommandInput]);

  const appendInputEntry = useCallback(
    (value: string) => {
      setEntries((current) => [
        ...current,
        {
          id: nextId(),
          type: 'input',
          prompt: terminalPrompt,
          value,
        },
      ]);
    },
    [nextId]
  );

  const appendLineEntries = useCallback(
    (lines: TerminalLine[]) => {
      setEntries((current) => [
        ...current,
        {
          id: nextId(),
          type: 'lines',
          lines,
        },
      ]);
    },
    [nextId]
  );

  const appendLinksEntry = useCallback(
    (title?: string) => {
      setEntries((current) => [
        ...current,
        {
          id: nextId(),
          type: 'links',
          title,
          links: portfolioContent.socialLinks,
        },
      ]);
    },
    [nextId]
  );

  const appendActionsEntry = useCallback(
    (title: string, actions: { label: string; command: string }[]) => {
      setEntries((current) => [
        ...current,
        {
          id: nextId(),
          type: 'actions',
          title,
          actions,
        },
      ]);
    },
    [nextId]
  );

  const appendProjectsEntry = useCallback(
    (title?: string) => {
      setEntries((current) => [
        ...current,
        {
          id: nextId(),
          type: 'projects',
          title,
          projects: portfolioContent.projects,
        },
      ]);
    },
    [nextId]
  );

  const gameLabel = useMemo(() => {
    if (!gameActive) {
      return '';
    }
    return gameActive.charAt(0).toUpperCase() + gameActive.slice(1);
  }, [gameActive]);

  const handleGameExit = useCallback(
    (score: string) => {
      if (!gameActive) {
        return;
      }

      const completedGame = gameActive;
      setGameActive(null);
      appendLineEntries([
        {
          text: `Game over. ${completedGame} score: ${score}`,
          tone: 'success',
        },
      ]);
      focusCommandInput();
    },
    [appendLineEntries, focusCommandInput, gameActive]
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    viewport.scrollTop = viewport.scrollHeight;
  }, [entries, matrixActive, bootComplete]);

  useEffect(
    () => () => {
      if (matrixTimeoutRef.current) {
        window.clearTimeout(matrixTimeoutRef.current);
      }
    },
    []
  );

  const typeLine = useCallback(
    async (line: TerminalLine) => {
      const targetText = line.text;
      const entryId = nextId();

      setEntries((current) => [
        ...current,
        {
          id: entryId,
          type: 'lines',
          lines: [{ ...line, text: '' }],
        },
      ]);
      setTypingCursor({ entryId, lineIndex: 0 });

      for (let charIndex = 1; charIndex <= targetText.length; charIndex += 1) {
        await delay(TYPEWRITER_DELAY_MS);
        const partialText = targetText.slice(0, charIndex);
        setEntries((current) =>
          current.map((entry) => {
            if (entry.id !== entryId || entry.type !== 'lines') {
              return entry;
            }

            return {
              ...entry,
              lines: [{ ...line, text: partialText }],
            };
          })
        );
      }
    },
    [nextId]
  );

  const printLines = useCallback(
    async (lines: TerminalLine[], animate = true) => {
      if (lines.length === 0) {
        return;
      }

      if (!animate) {
        appendLineEntries(lines);
        return;
      }

      setIsBusy(true);
      try {
        for (const line of lines) {
          await typeLine(line);
        }
      } finally {
        setTypingCursor(null);
        setIsBusy(false);
      }
    },
    [appendLineEntries, typeLine]
  );

  const printLinksEntry = useCallback(
    async (title: string) => {
      setIsBusy(true);
      try {
        await typeLine({ text: title });
        appendLinksEntry();
      } finally {
        setTypingCursor(null);
        setIsBusy(false);
      }
    },
    [appendLinksEntry, typeLine]
  );

  const printNeofetchEntry = useCallback(
    async (rows: { left: string; label?: string; value: string }[]) => {
      if (rows.length === 0) {
        return;
      }

      const entryId = nextId();
      setIsBusy(true);

      try {
        setEntries((current) => [
          ...current,
          {
            id: entryId,
            type: 'neofetch',
            rows: rows.map((row) => ({ ...row, left: '', value: '' })),
          },
        ]);

        for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
          const targetRow = rows[rowIndex];
          const maxChars = Math.max(targetRow.left.length, targetRow.value.length);

          if (maxChars === 0) {
            continue;
          }

          for (let charIndex = 1; charIndex <= maxChars; charIndex += 1) {
            await delay(TYPEWRITER_DELAY_MS);

            setEntries((current) =>
              current.map((entry) => {
                if (entry.id !== entryId || entry.type !== 'neofetch') {
                  return entry;
                }

                return {
                  ...entry,
                  rows: entry.rows.map((row, index) => {
                    if (index !== rowIndex) {
                      return row;
                    }

                    return {
                      ...row,
                      left: targetRow.left.slice(0, charIndex),
                      label: targetRow.label,
                      value: targetRow.value.slice(0, charIndex),
                    };
                  }),
                };
              })
            );
          }
        }
      } finally {
        setTypingCursor(null);
        setIsBusy(false);
      }
    },
    [nextId]
  );

  const printProjectsEntry = useCallback(
    async (title: string) => {
      setIsBusy(true);
      try {
        await typeLine({ text: title });
        appendProjectsEntry();
      } finally {
        setTypingCursor(null);
        setIsBusy(false);
      }
    },
    [appendProjectsEntry, typeLine]
  );

  useEffect(() => {
    if (!bootComplete || welcomeShown) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const banner = viewportWidth < 520 ? [] : viewportWidth < 820 ? asciiHeader.compact : asciiHeader.full;

    const introLines: TerminalLine[] = [];

    if (banner.length > 0) {
      introLines.push(...banner.map((text) => ({ text })));
    } else {
      introLines.push({ text: 'Andrew Kellogg' });
    }

    introLines.push({ text: asciiHeader.subtitle, tone: 'success' });
    introLines.push({ text: asciiHeader.summary });
    introLines.push({ text: '' });
    introLines.push({ text: asciiHeader.separator });
    introLines.push({ text: bootHint, tone: 'success' });

    appendLineEntries(introLines);
    setWelcomeShown(true);
  }, [appendLineEntries, bootComplete, welcomeShown]);

  const runMeltdown = useCallback(async () => {
    if (isBusyRef.current) {
      return;
    }

    setIsBusy(true);
    setMeltdownActive(true);

    const crashLines: { text: string; tone?: TerminalLine['tone']; wait: number }[] = [
      { text: 'rm: removing all files...', tone: 'warning', wait: 220 },
      { text: 'kernel panic: filesystem integrity compromised', tone: 'error', wait: 220 },
      { text: 'critical fault at 0x0000:DEAD:BEEF', tone: 'error', wait: 190 },
      { text: 'attempting emergency rollback...', tone: 'warning', wait: 240 },
      { text: 'restoring terminal state from backup snapshot', tone: 'success', wait: 320 },
    ];

    for (const line of crashLines) {
      appendLineEntries([{ text: line.text, tone: line.tone }]);
      await delay(line.wait);
    }

    await delay(MELTDOWN_RECOVERY_DELAY_MS);
    setEntries([]);
    appendLineEntries([
      { text: 'System recovered. No files were harmed.', tone: 'success' },
      { text: bootHint },
    ]);

    setMeltdownActive(false);
    setIsBusy(false);
  }, [appendLineEntries]);

  const getPipeSourceLines = useCallback(
    async (rawCommand: string, historySnapshot: CommandHistoryItem[]): Promise<TerminalLine[]> => {
      const command = rawCommand.trim();
      const lowered = command.toLowerCase();

      if (lowered === 'help') {
        return commandDefinitions.map((item) => ({ text: `${item.name.padEnd(10, ' ')} ${item.description}` }));
      }

      if (lowered === 'neofetch') {
        return buildNeofetchRows(theme).map((row) => ({
          text: `${row.left.padEnd(18, ' ')}${row.label ? `${row.label} ${row.value}` : row.value}`,
        }));
      }

      if (lowered === 'about') {
        return [
          { text: `${portfolioContent.name} | ${portfolioContent.title}` },
          { text: `Location: ${portfolioContent.location}` },
          ...portfolioContent.about.map((line) => ({ text: line })),
        ];
      }

      if (lowered === 'skills') {
        return [{ text: 'Skill stack:' }, ...portfolioContent.skills.map((skill) => ({ text: `- ${skill}` }))];
      }

      if (lowered === 'stack') {
        return [{ text: 'Tools and platforms:' }, ...portfolioContent.stack.map((line) => ({ text: `- ${line}` }))];
      }

      if (lowered === 'links') {
        return [
          { text: 'Links:' },
          ...portfolioContent.socialLinks.map((link) => ({ text: `${link.label}: ${link.url}` })),
        ];
      }

      if (lowered === 'projects') {
        return [
          { text: 'Project highlights:' },
          ...portfolioContent.projects.flatMap((project) => {
            const lines = [
              { text: `- ${project.name}` },
              { text: `  ${project.summary}` },
            ];

            if (project.url) {
              lines.push({ text: `  ${project.linkLabel ?? 'Link'}: ${project.url}` });
            }

            return lines;
          }),
        ];
      }

      if (lowered === 'contact') {
        return [{ text: 'Contact:' }, ...portfolioContent.contact.map((line) => ({ text: `- ${line}` }))];
      }

      if (lowered === 'whoami') {
        return [{ text: 'andrew_kellogg — Growth Engineer / Del Mar Advertising / Las Vegas, NV' }];
      }

      if (lowered === 'cat') {
        return [{ text: 'Usage: cat <filename>. Try: cat readme.txt' }];
      }

      if (lowered === 'cat readme.txt') {
        return readmeContent.map((line) => ({ text: line }));
      }

      if (lowered.startsWith('cat ')) {
        return [{ text: `cat: ${command.slice(4)}: No such file or directory`, tone: 'error' }];
      }

      if (lowered === 'theme') {
        return [{ text: `Current theme: ${theme}. Available: amber, green, blue, white` }];
      }

      if (lowered.startsWith('theme ')) {
        const requestedTheme = lowered.slice(6).trim();
        if (!isThemeName(requestedTheme)) {
          return [{ text: 'Invalid theme. Use: theme amber|green|blue|white', tone: 'error' }];
        }

        return [{ text: `Theme switched to ${requestedTheme}.`, tone: 'success' }];
      }

      if (lowered === 'weather') {
        return [await fetchWeatherLine()];
      }

      if (lowered === 'history') {
        if (historySnapshot.length === 0) {
          return [{ text: 'No command history yet.' }];
        }

        return historySnapshot.map((item, index) => ({
          text: `${index + 1}. [${formatHistoryTimestamp(item.timestamp)}] ${item.command}`,
        }));
      }

      if (lowered === 'ls') {
        return [
          {
            text: commandDefinitions
              .map((item) => item.name.replace(/\s+/g, '_'))
              .join('  '),
          },
        ];
      }

      const playResolution = resolvePlayCommand(lowered);
      if (playResolution.action === 'list' || playResolution.action === 'invalid') {
        return [{ text: playResolution.message, tone: playResolution.action === 'invalid' ? 'error' : undefined }];
      }

      if (playResolution.action === 'start') {
        return [{ text: `Run "${rawCommand.trim()}" directly to launch games.` }];
      }

      if (lowered === 'matrix') {
        return [{ text: 'Entering the matrix...', tone: 'success' }];
      }

      if (lowered.startsWith('sudo')) {
        return [{ text: 'Nice try.', tone: 'warning' }];
      }

      if (lowered === 'rm -rf /') {
        return [{ text: 'rm: removing all files...', tone: 'warning' }];
      }

      return [{ text: `Command not found: ${command}. Type help to list valid commands.`, tone: 'error' }];
    },
    [buildNeofetchRows, fetchWeatherLine, formatHistoryTimestamp, theme]
  );

  const runPipeCommand = useCallback((sourceLines: TerminalLine[], pipeTarget: string): TerminalLine[] => {
    const loweredPipeTarget = pipeTarget.toLowerCase();

    if (loweredPipeTarget.startsWith('grep')) {
      const term = pipeTarget.slice(4).trim().toLowerCase();

      if (!term) {
        return [{ text: 'Usage: grep <term>', tone: 'error' }];
      }

      const filtered = sourceLines.filter((line) => line.text.toLowerCase().includes(term));
      return filtered.length > 0 ? filtered : [{ text: 'No matches found.' }];
    }

    if (loweredPipeTarget === 'wc') {
      const output = sourceLines.map((line) => line.text).join('\n');
      const lineCount = sourceLines.length;
      const wordCount = output.trim() ? output.trim().split(/\s+/).length : 0;
      const charCount = output.length;
      return [{ text: `${lineCount} ${wordCount} ${charCount}` }];
    }

    return [{ text: 'Unsupported pipe command', tone: 'error' }];
  }, []);

  const runCommand = useCallback(
    async (rawCommand: string) => {
      if (isBusyRef.current) {
        return;
      }

      const command = rawCommand.trim();
      const lowered = command.toLowerCase();

      if (!command) {
        return;
      }

      appendInputEntry(command);

      const historySnapshot = [...commandHistory, { command, timestamp: Date.now() }];
      setCommandHistory(historySnapshot);
      setHistoryIndex(null);

      if (lowered === 'clear') {
        setEntries([]);
        window.location.hash = '';
        return;
      }

      window.location.hash = command;

      if (command.includes('|')) {
        const pipeParts = command.split('|').map((part) => part.trim());
        if (pipeParts.length !== 2 || !pipeParts[0] || !pipeParts[1]) {
          await printLines([{ text: 'Invalid pipe syntax', tone: 'error' }]);
          return;
        }

        const sourceLines = await getPipeSourceLines(pipeParts[0], historySnapshot);
        await printLines(runPipeCommand(sourceLines, pipeParts[1]));
        return;
      }

      if (lowered === 'help') {
        const lines = commandDefinitions.map((item) => ({
          text: `${item.name.padEnd(10, ' ')} ${item.description}`,
        }));
        await printLines(lines);
        return;
      }

      if (lowered === 'neofetch') {
        await printNeofetchEntry(buildNeofetchRows(theme));
        return;
      }

      if (lowered === 'about') {
        await printLines([
          { text: `${portfolioContent.name} | ${portfolioContent.title}` },
          { text: `Location: ${portfolioContent.location}` },
          ...portfolioContent.about.map((line) => ({ text: line })),
        ]);
        return;
      }

      if (lowered === 'skills') {
        await printLines([{ text: 'Skill stack:' }, ...portfolioContent.skills.map((skill) => ({ text: `- ${skill}` }))]);
        return;
      }

      if (lowered === 'stack') {
        await printLines([{ text: 'Tools and platforms:' }, ...portfolioContent.stack.map((line) => ({ text: `- ${line}` }))]);
        return;
      }

      if (lowered === 'links') {
        await printLinksEntry('Links:');
        return;
      }

      if (lowered === 'projects') {
        await printProjectsEntry('Project highlights:');
        return;
      }

      if (lowered === 'contact') {
        await printLines([{ text: 'Contact:' }, ...portfolioContent.contact.map((line) => ({ text: `- ${line}` }))]);
        await printLinksEntry('Quick links:');
        return;
      }

      if (lowered === 'whoami') {
        await printLines([{ text: 'andrew_kellogg — Growth Engineer / Del Mar Advertising / Las Vegas, NV' }]);
        return;
      }

      if (lowered === 'cat') {
        await printLines([{ text: 'Usage: cat <filename>. Try: cat readme.txt' }]);
        return;
      }

      if (lowered === 'cat readme.txt') {
        await printLines(readmeContent.map((line) => ({ text: line })));
        return;
      }

      if (lowered.startsWith('cat ')) {
        await printLines([{ text: `cat: ${command.slice(4)}: No such file or directory`, tone: 'error' }]);
        return;
      }

      if (lowered === 'theme') {
        await printLines([{ text: `Current theme: ${theme}. Choose a theme below or run: theme amber|green|blue|white` }]);
        appendActionsEntry('Available themes:', themeActions);
        return;
      }

      if (lowered.startsWith('theme ')) {
        const requestedTheme = lowered.slice(6).trim();

        if (!isThemeName(requestedTheme)) {
          await printLines([{ text: 'Invalid theme. Use: theme amber|green|blue|white', tone: 'error' }]);
          return;
        }

        setTheme(requestedTheme);
        await printLines([{ text: `Theme switched to ${requestedTheme}.`, tone: 'success' }]);
        return;
      }

      if (lowered === 'weather') {
        await printLines([{ text: 'Fetching weather data...' }]);
        await printLines([await fetchWeatherLine()]);
        return;
      }

      if (lowered === 'history') {
        if (historySnapshot.length === 0) {
          await printLines([{ text: 'No command history yet.' }]);
          return;
        }

        await printLines(
          historySnapshot.map((item, index) => ({
            text: `${index + 1}. [${formatHistoryTimestamp(item.timestamp)}] ${item.command}`,
          }))
        );
        return;
      }

      if (lowered === 'ls') {
        await printLines([
          {
            text: commandDefinitions
              .map((item) => item.name.replace(/\s+/g, '_'))
              .join('  '),
          },
        ]);
        return;
      }

      const playResolution = resolvePlayCommand(lowered);
      if (playResolution.action === 'list') {
        appendActionsEntry('Available games:', gameActions);
        return;
      }

      if (playResolution.action === 'invalid') {
        await printLines([{ text: playResolution.message, tone: 'error' }]);
        return;
      }

      if (playResolution.action === 'start') {
        setGameActive(playResolution.game);
        return;
      }

      if (lowered === 'matrix') {
        setMatrixActive(true);
        await printLines([{ text: 'Entering the matrix...', tone: 'success' }]);
        if (matrixTimeoutRef.current) {
          window.clearTimeout(matrixTimeoutRef.current);
        }
        matrixTimeoutRef.current = window.setTimeout(() => {
          setMatrixActive(false);
          appendLineEntries([{ text: 'Matrix session ended.' }]);
          matrixTimeoutRef.current = null;
        }, MATRIX_DURATION_MS);
        return;
      }

      if (lowered.startsWith('sudo')) {
        await printLines([{ text: 'Nice try.', tone: 'warning' }]);
        return;
      }

      if (lowered === 'rm -rf /') {
        await runMeltdown();
        return;
      }

      await printLines([
        {
          text: `Command not found: ${command}. Type help to list valid commands.`,
          tone: 'error',
        },
      ]);
    },
    [
      appendInputEntry,
      appendActionsEntry,
      appendLineEntries,
      buildNeofetchRows,
      commandHistory,
      fetchWeatherLine,
      formatHistoryTimestamp,
      getPipeSourceLines,
      printLines,
      printLinksEntry,
      printNeofetchEntry,
      printProjectsEntry,
      runMeltdown,
      runPipeCommand,
      theme,
    ]
  );

  useEffect(() => {
    if (!bootComplete || !welcomeShown || hashCommandLoaded) {
      return;
    }

    const hashCommand = decodeURIComponent(window.location.hash.replace(/^#/, '')).trim();
    setHashCommandLoaded(true);

    if (!hashCommand) {
      return;
    }

    void runCommand(hashCommand);
  }, [bootComplete, hashCommandLoaded, runCommand, welcomeShown]);

  const handleSubmit = () => {
    void runCommand(inputValue);
    setInputValue('');
    focusCommandInput();
  };

  const handleHistoryNavigate = (direction: 'up' | 'down') => {
    if (commandHistory.length === 0) {
      return;
    }

    if (direction === 'up') {
      setHistoryIndex((currentIndex) => {
        const nextIndex = currentIndex === null ? commandHistory.length - 1 : Math.max(currentIndex - 1, 0);
        setInputValue(commandHistory[nextIndex].command);
        return nextIndex;
      });
      return;
    }

    setHistoryIndex((currentIndex) => {
      if (currentIndex === null) {
        setInputValue('');
        return null;
      }

      const nextIndex = currentIndex + 1;

      if (nextIndex >= commandHistory.length) {
        setInputValue('');
        return null;
      }

      setInputValue(commandHistory[nextIndex].command);
      return nextIndex;
    });
  };

  const handleTabComplete = () => {
    if (isBusyRef.current) {
      return;
    }

    const prefix = inputValue.trim().toLowerCase();

    if (!prefix) {
      return;
    }

    const matches = commandDefinitions.map((commandItem) => commandItem.name).filter((name) => name.startsWith(prefix));

    if (matches.length === 1) {
      setInputValue(matches[0]);
      return;
    }

    if (matches.length > 1) {
      void printLines([{ text: 'Completions:' }, ...matches.map((match) => ({ text: `- ${match}` }))]);
    }
  };

  const runButtonCommand = (command: string) => {
    if (command === 'theme') {
      appendActionsEntry('Available themes:', themeActions);
      focusCommandInput();
      return;
    }

    setInputValue(command);
    void runCommand(command);
    setInputValue('');
    focusCommandInput();
  };

  const handleInlineActionCommand = (command: string) => {
    void runCommand(command);
    focusCommandInput();
  };

  return (
    <main className="app-shell" style={shellStyle}>
      <MatrixRain active={matrixActive} />
      <section className={`terminal-window ${meltdownActive ? 'meltdown' : ''}`.trim()}>
        <header className="terminal-header">andrewkellogg.com :: growth engineer terminal :: {headerClock}</header>
        <div className="terminal-viewport" ref={viewportRef}>
          {!bootComplete ? (
            <BootSequence skip={skipBoot} onComplete={handleBootComplete} />
          ) : gameActive ? (
            <div className="game-overlay" role="application" aria-label={`${gameLabel} game`}>
              <div className="game-overlay-header">
                {gameLabel} :: keyboard controls active :: ESC to quit
              </div>
              {gameActive === 'snake' ? (
                <SnakeGame
                  fgColor={activeTheme.fg}
                  bgColor={activeTheme.bg}
                  softColor={activeTheme.fgSoft}
                  onExit={handleGameExit}
                />
              ) : null}
              {gameActive === 'pong' ? (
                <PongGame
                  fgColor={activeTheme.fg}
                  bgColor={activeTheme.bg}
                  softColor={activeTheme.fgSoft}
                  onExit={handleGameExit}
                />
              ) : null}
              {gameActive === 'dino' ? (
                <DinoGame
                  fgColor={activeTheme.fg}
                  bgColor={activeTheme.bg}
                  softColor={activeTheme.fgSoft}
                  onExit={handleGameExit}
                />
              ) : null}
              {gameActive === 'asteroids' ? (
                <AsteroidsGame
                  fgColor={activeTheme.fg}
                  bgColor={activeTheme.bg}
                  softColor={activeTheme.fgSoft}
                  onExit={handleGameExit}
                />
              ) : null}
              {gameActive === 'invaders' ? (
                <SpaceInvadersGame
                  fgColor={activeTheme.fg}
                  bgColor={activeTheme.bg}
                  softColor={activeTheme.fgSoft}
                  onExit={handleGameExit}
                />
              ) : null}
            </div>
          ) : (
            <>
              <CommandOutput
                entries={entries}
                actionsDisabled={isBusy}
                onActionCommand={handleInlineActionCommand}
                typingCursor={typingCursor}
              />
              <CommandInput
                prompt={terminalPrompt}
                value={inputValue}
                disabled={isBusy}
                inputRef={commandInputRef}
                onChange={setInputValue}
                onSubmit={handleSubmit}
                onHistoryNavigate={handleHistoryNavigate}
                onTabComplete={handleTabComplete}
              />
              <div className="command-buttons" aria-label="Quick commands">
                {primaryCommandButtons.map((action) => (
                  <button type="button" key={action.command} onClick={() => runButtonCommand(action.command)} disabled={isBusy}>
                    {action.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
